import React, { useEffect, useMemo, useState } from 'react'
import { Button, Box, Form, Columns, Element, Message } from 'react-bulma-components'
import Select from 'react-select'
import {
  InputFormula,
  InputFormulaPath,
  InputFormulaRule,
  InputFormulaOperation,
  InputFormulaEventPath,
} from '../../../api/employer-rules'
import {
  isInputFormulaPath,
  isInputFormulaRule,
  isInputFormulaOperation,
  isInputFormulaEventPath,
  pathStringToFormula,
} from './input-formula-utils'
import { PathOption } from './types'

/**
 * Props for FormulaBuilder component
 */
interface FormulaBuilderProps {
  value: InputFormula | null
  onChange: (formula: InputFormula | null) => void
  availablePaths: PathOption[]
  availableRules?: Array<{ value: string; label: string }>
  availableEvents?: Array<{ value: string; label: string }>
  disabled?: boolean
  error?: string
}

/**
 * Type for formula element in the builder.
 * eventPath represents a path filtered by specific work period events.
 */
type FormulaElement =
  | { type: 'path'; path: string }
  | { type: 'rule'; ruleId: string }
  | { type: 'operator'; op: 'add' | 'sub' }
  | {
      type: 'eventPath'
      path: string
      workPeriodEventIds: string[]
      excludeIfWorked?: boolean
    }

/**
 * Expected next token type when building a linear formula.
 * This is used to drive button enabled/disabled states.
 */
type ExpectedNextElement = 'operand' | 'operator'

/**
 * Validation status for the linear editor state.
 * - 'valid': elements can be converted to an AST
 * - 'incomplete': sequence is structurally correct so far, but missing a trailing operand
 * - 'invalid': sequence is structurally incorrect and cannot be repaired by appending
 */
type ElementsValidationStatus = 'valid' | 'incomplete' | 'invalid'

/**
 * Result of validating the linear formula elements.
 */
interface ElementsValidationResult {
  status: ElementsValidationStatus
  expectedNext: ExpectedNextElement
  message?: string
}

/**
 * Returns true when the element is an operand (path/rule).
 */
function isOperandElement(el: FormulaElement): el is Exclude<FormulaElement, { type: 'operator' }> {
  return el.type === 'path' || el.type === 'rule' || el.type === 'eventPath'
}

/**
 * Validates the "linear editor" representation of the formula.
 *
 * The editor expects an alternating sequence:
 * - operand / operator / operand / ...
 *
 * This is separate from validating the AST itself (which is handled elsewhere).
 * Here we validate that the UI state can be converted to a sane AST and provide
 * a user-facing message to explain what's wrong.
 */
function validateFormulaElements(
  els: FormulaElement[],
  availablePaths: PathOption[],
  availableRules: Array<{ value: string; label: string }>,
): ElementsValidationResult {
  // Empty is a valid starting state: user must add an operand first.
  if (els.length === 0) {
    return { status: 'incomplete', expectedNext: 'operand' }
  }

  const availablePathValues = new Set(availablePaths.map(p => p.value))
  const availableRuleValues = new Set(availableRules.map(r => r.value))

  // Validate structural alternation by position.
  for (let i = 0; i < els.length; i++) {
    const el = els[i]
    if (!el) {
      // Defensive guard (noUncheckedIndexedAccess).
      return {
        status: 'invalid',
        expectedNext: i % 2 === 0 ? 'operand' : 'operator',
        message: 'La formule est invalide.',
      }
    }
    const shouldBeOperand = i % 2 === 0

    if (shouldBeOperand && el.type === 'operator') {
      return {
        status: 'invalid',
        expectedNext: 'operand',
        message:
          i === 0
            ? "La formule ne peut pas commencer par un opérateur. Ajoutez d'abord un élément (path, règle)."
            : "Un élément (path, règle) est requis après un opérateur. Supprimez l'élément en trop pour corriger.",
      }
    }

    if (!shouldBeOperand && el.type !== 'operator') {
      return {
        status: 'invalid',
        expectedNext: 'operator',
        message:
          "Un opérateur est requis entre chaque élément. Supprimez l'élément en trop pour corriger.",
      }
    }

    // Validate operand content (helps avoid silently producing null).
    if (isOperandElement(el)) {
      if (el.type === 'path') {
        if (!el.path) {
          return {
            status: 'invalid',
            expectedNext: 'operator',
            message: 'Le path ne peut pas être vide.',
          }
        }
        if (availablePathValues.size > 0 && !availablePathValues.has(el.path)) {
          return {
            status: 'invalid',
            expectedNext: 'operator',
            message: `Le path "${el.path}" n'est pas valide pour ce contexte.`,
          }
        }
      }

      if (el.type === 'rule') {
        if (!el.ruleId) {
          return {
            status: 'invalid',
            expectedNext: 'operator',
            message: 'La règle référencée ne peut pas être vide.',
          }
        }
        if (availableRuleValues.size > 0 && !availableRuleValues.has(el.ruleId)) {
          return {
            status: 'invalid',
            expectedNext: 'operator',
            message: `La règle "${el.ruleId}" n'est pas disponible.`,
          }
        }
      }

      if (el.type === 'eventPath') {
        if (!el.path) {
          return {
            status: 'invalid',
            expectedNext: 'operator',
            message: "Le path de l'événement ne peut pas être vide.",
          }
        }
        if (availablePathValues.size > 0 && !availablePathValues.has(el.path)) {
          return {
            status: 'invalid',
            expectedNext: 'operator',
            message: `Le path "${el.path}" n'est pas valide pour ce contexte.`,
          }
        }
        if (!el.workPeriodEventIds || el.workPeriodEventIds.length === 0) {
          return {
            status: 'invalid',
            expectedNext: 'operator',
            message: 'Au moins un événement doit être sélectionné.',
          }
        }
      }
    }
  }

  // If the sequence length is even, we end with an operator → incomplete but structurally OK.
  const expectedNext: ExpectedNextElement = els.length % 2 === 0 ? 'operand' : 'operator'
  if (expectedNext === 'operand') {
    return {
      status: 'incomplete',
      expectedNext,
      message: "La formule est incomplète. Ajoutez un élément (path, règle) après l'opérateur.",
    }
  }

  return { status: 'valid', expectedNext }
}

/**
 * Converts one operand element to its AST representation.
 * Returns null when the element is not a valid operand.
 */
function operandElementToFormula(el: FormulaElement): InputFormula | null {
  if (el.type === 'path') {
    return { type: 'path', path: el.path } as InputFormulaPath
  }
  if (el.type === 'rule') {
    return { type: 'rule', ruleId: el.ruleId } as InputFormulaRule
  }
  if (el.type === 'eventPath') {
    const formula: InputFormulaEventPath = {
      type: 'eventPath',
      path: el.path,
      workPeriodEventIds: el.workPeriodEventIds,
    }
    if (el.excludeIfWorked) formula.excludeIfWorked = true
    return formula
  }
  return null
}

/**
 * Converts a validated linear sequence into an AST.
 *
 * Implementation notes:
 * - We use a left-fold to support mixed '+' and '-' (e.g. "a + b - c")
 * - We keep n-ary operations for consecutive identical operators (e.g. "a + b + c")
 */
function linearElementsToFormula(els: FormulaElement[]): InputFormula | null {
  if (els.length === 0) return null

  // We only convert if the sequence is complete (ends with an operand).
  if (els.length % 2 === 0) return null

  const firstEl = els[0]
  if (!firstEl) return null

  const first = operandElementToFormula(firstEl)
  if (!first) return null

  let result: InputFormula = first

  for (let i = 1; i < els.length; i += 2) {
    const operatorEl = els[i]
    const operandEl = els[i + 1]
    if (!operatorEl || operatorEl.type !== 'operator' || !operandEl) return null

    const nextOperand = operandElementToFormula(operandEl)
    if (!nextOperand) return null

    // If we already have the same operation, append to keep the tree shallow.
    if (isInputFormulaOperation(result) && result.op === operatorEl.op) {
      result = {
        op: result.op,
        args: [...result.args, nextOperand],
      } as InputFormulaOperation
      continue
    }

    // Otherwise, create a nested binary operation.
    result = {
      op: operatorEl.op,
      args: [result, nextOperand],
    } as InputFormulaOperation
  }

  return result
}

/**
 * FormulaBuilder component - Visual builder for complex formulas
 * Provides both simple and advanced modes for formula creation
 */
const FormulaBuilder: React.FC<FormulaBuilderProps> = ({
  value,
  onChange,
  availablePaths,
  availableRules = [],
  availableEvents = [],
  disabled = false,
  error,
}) => {
  /**
   * We default to advanced mode when an existing formula is NOT a simple path.
   * This avoids showing the "simple path select" empty when loading an existing complex formula.
   */
  const [advancedMode, setAdvancedMode] = useState<boolean>(() =>
    Boolean(value && !isInputFormulaPath(value)),
  )
  const [elements, setElements] = useState<FormulaElement[]>(() => {
    // Initialize editor state from the incoming formula (if any).
    return value ? formulaToElements(value) : []
  })

  // Check if current formula is simple (just a path)
  const isSimpleFormula = value && isInputFormulaPath(value)

  /**
   * Validate the linear editor state to:
   * - drive button enable/disable logic
   * - provide a clear message when the formula is incomplete/invalid
   */
  const elementsValidation = useMemo(
    () => validateFormulaElements(elements, availablePaths, availableRules),
    [elements, availablePaths, availableRules],
  )

  /**
   * Keep the UI mode in sync when the form loads an existing rule asynchronously.
   *
   * Typical scenario:
   * - component mounts with value = null
   * - later, formik sets value to a complex InputFormula (operation/rule)
   * Without this effect, the UI stays in simple mode and shows an empty select.
   */
  useEffect(() => {
    if (!value) return

    // Auto-switch to advanced mode for non-path formulas.
    if (!advancedMode && !isInputFormulaPath(value)) {
      setAdvancedMode(true)
      setElements(formulaToElements(value))
    }
  }, [value, advancedMode])

  /**
   * Converts InputFormula to array of FormulaElements for editing
   */
  function formulaToElements(formula: InputFormula): FormulaElement[] {
    const result: FormulaElement[] = []

    if (isInputFormulaPath(formula)) {
      result.push({ type: 'path', path: formula.path })
    } else if (isInputFormulaRule(formula)) {
      result.push({ type: 'rule', ruleId: formula.ruleId })
    } else if (isInputFormulaEventPath(formula)) {
      result.push({
        type: 'eventPath',
        path: formula.path,
        workPeriodEventIds: formula.workPeriodEventIds,
        excludeIfWorked: formula.excludeIfWorked,
      })
    } else if (isInputFormulaOperation(formula)) {
      formula.args.forEach((arg, index) => {
        if (index > 0) {
          result.push({ type: 'operator', op: formula.op })
        }
        const argElements = formulaToElements(arg)
        result.push(...argElements)
      })
    }

    return result
  }

  /**
   * Converts array of FormulaElements to InputFormula AST
   */
  function elementsToFormula(els: FormulaElement[]): InputFormula | null {
    // Gate conversion behind the linear validation to avoid silently returning null.
    // Note: we re-validate here because this function is also called from event handlers.
    const validation = validateFormulaElements(els, availablePaths, availableRules)
    if (validation.status !== 'valid') return null

    return linearElementsToFormula(els)
  }

  /**
   * Handles simple mode change (single path selection)
   */
  const handleSimpleModeChange = (path: string) => {
    if (!path) {
      onChange(null)
    } else {
      onChange(pathStringToFormula(path))
    }
  }

  /**
   * Handles switching to advanced mode
   */
  const handleSwitchToAdvanced = () => {
    setAdvancedMode(true)
    // Initialize elements from current value
    if (value) {
      setElements(formulaToElements(value))
    } else {
      setElements([])
    }
  }

  /**
   * Handles switching back to simple mode
   */
  const handleSwitchToSimple = () => {
    setAdvancedMode(false)
    // Clear elements and reset to simple path if applicable
    if (value && isInputFormulaPath(value)) {
      // Keep the path
    } else {
      onChange(null)
    }
  }

  /**
   * Adds a new element to the formula
   */
  const handleAddElement = (elementType: 'path' | 'rule' | 'operator' | 'eventPath') => {
    const newElements = [...elements]

    const expectsOperand = elementsValidation.expectedNext === 'operand'
    const expectsOperator = elementsValidation.expectedNext === 'operator'

    if (elementType === 'operator' && !expectsOperator) {
      return
    }
    if (elementType !== 'operator' && !expectsOperand) {
      return
    }

    if (elementType === 'path') {
      newElements.push({ type: 'path', path: availablePaths[0]?.value || '' })
    } else if (elementType === 'rule') {
      if (availableRules.length === 0) return
      newElements.push({ type: 'rule', ruleId: availableRules[0]?.value || '' })
    } else if (elementType === 'eventPath') {
      if (availableEvents.length === 0) return
      newElements.push({
        type: 'eventPath',
        path: availablePaths[0]?.value || '',
        workPeriodEventIds: [],
        excludeIfWorked: false,
      })
    } else if (elementType === 'operator') {
      newElements.push({ type: 'operator', op: 'add' })
    }

    setElements(newElements)
    onChange(elementsToFormula(newElements))
  }

  /**
   * Updates an element at a specific index
   */
  const handleUpdateElement = (index: number, updatedElement: FormulaElement) => {
    const newElements = [...elements]
    newElements[index] = updatedElement
    setElements(newElements)
    onChange(elementsToFormula(newElements))
  }

  /**
   * Removes an element at a specific index
   */
  const handleRemoveElement = (index: number) => {
    const newElements = elements.filter((_, idx) => idx !== index)
    setElements(newElements)
    onChange(elementsToFormula(newElements))
  }

  // Render Simple Mode
  if (!advancedMode) {
    return (
      <Box>
        <Form.Field>
          <Form.Label>Formule d'input (mode simple)</Form.Label>
          <Form.Control>
            <Form.Select
              value={isSimpleFormula && value ? value.path : ''}
              onChange={e => handleSimpleModeChange(e.target.value)}
              disabled={disabled}
              className={error ? 'is-danger' : ''}
              data-test="input-formula-simple-select"
            >
              <option value="">Sélectionner un path</option>
              {availablePaths.map(pathOption => (
                <option key={pathOption.value} value={pathOption.value}>
                  {pathOption.label}
                </option>
              ))}
            </Form.Select>
          </Form.Control>
          <Form.Help className="help">
            Sélectionnez un path simple pour l'input de la règle
          </Form.Help>
        </Form.Field>

        <Button
          size="small"
          onClick={handleSwitchToAdvanced}
          disabled={disabled}
          data-test="input-formula-switch-advanced"
        >
          🔧 Passer en mode avancé
        </Button>
      </Box>
    )
  }

  // Render Advanced Mode
  const advancedMessageColor =
    error || elementsValidation.status === 'invalid' ? 'danger' : 'warning'

  return (
    <Box>
      <Element renderAs="div" mb={3}>
        <Form.Label>Formule d'input (mode avancé)</Form.Label>
        <Button
          size="small"
          onClick={handleSwitchToSimple}
          disabled={disabled}
          data-test="input-formula-switch-simple"
        >
          ⬅️ Retour au mode simple
        </Button>
      </Element>

      {/* Show a clear message when the linear formula is incomplete/invalid (and also surface parent form error). */}
      {(error || elementsValidation.message) && (
        <Message color={advancedMessageColor} mb={3}>
          <Message.Body>
            {error && (
              <div>
                <strong>Erreur :</strong> {error}
              </div>
            )}
            {elementsValidation.message && (
              <div>
                <strong>Validation :</strong> {elementsValidation.message}
              </div>
            )}
          </Message.Body>
        </Message>
      )}

      {/* Display current elements */}
      {elements.length > 0 && (
        <Box backgroundColor="light" mb={3}>
          {elements.map((element, index) => (
            <Columns key={index} vCentered mb={2}>
              <Columns.Column size={1}>
                <Element renderAs="strong" style={{ fontSize: '1.2em' }}>
                  {element.type === 'operator'
                    ? element.op === 'add'
                      ? '+'
                      : '-'
                    : index === 0
                      ? ''
                      : ''}
                </Element>
              </Columns.Column>

              <Columns.Column>
                {element.type === 'path' && (
                  <Form.Select
                    value={element.path}
                    onChange={e =>
                      handleUpdateElement(index, { type: 'path', path: e.target.value })
                    }
                    disabled={disabled}
                    data-test={`input-formula-path-${index}`}
                  >
                    {availablePaths.map(pathOption => (
                      <option key={pathOption.value} value={pathOption.value}>
                        {pathOption.label}
                      </option>
                    ))}
                  </Form.Select>
                )}

                {element.type === 'rule' && (
                  <Form.Select
                    value={element.ruleId}
                    onChange={e =>
                      handleUpdateElement(index, { type: 'rule', ruleId: e.target.value })
                    }
                    disabled={disabled}
                    data-test={`input-formula-rule-${index}`}
                  >
                    {availableRules.length === 0 && (
                      <option value="">Aucune règle disponible</option>
                    )}
                    {availableRules.map(rule => (
                      <option key={rule.value} value={rule.value}>
                        📋 {rule.label}
                      </option>
                    ))}
                  </Form.Select>
                )}

                {element.type === 'eventPath' && (
                  <Box style={{ padding: '0.75rem' }}>
                    <Form.Field>
                      <Form.Label size="small">Valeur</Form.Label>
                      <Form.Select
                        value={element.path}
                        onChange={e =>
                          handleUpdateElement(index, {
                            ...element,
                            path: e.target.value,
                          })
                        }
                        disabled={disabled}
                        data-test={`input-formula-eventpath-path-${index}`}
                      >
                        {availablePaths.map(p => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Field>
                    <Form.Field>
                      <Form.Label size="small">Événements</Form.Label>
                      <div data-test={`input-formula-eventpath-events-${index}`}>
                        <Select
                          isMulti
                          isDisabled={disabled}
                          classNamePrefix="select"
                          value={availableEvents
                            .filter(ev => element.workPeriodEventIds.includes(ev.value))
                            .map(ev => ({ value: ev.value, label: ev.label }))}
                          onChange={selected => {
                            const ids = (selected || []).map((s: any) => s.value)
                            handleUpdateElement(index, {
                              ...element,
                              workPeriodEventIds: ids,
                            })
                          }}
                          options={availableEvents.map(ev => ({
                            value: ev.value,
                            label: ev.label,
                          }))}
                          placeholder="Sélectionner les événements..."
                          noOptionsMessage={() => 'Aucun événement disponible'}
                        />
                      </div>
                    </Form.Field>
                    <Form.Field>
                      <label className="checkbox" style={{ fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={element.excludeIfWorked || false}
                          disabled={disabled}
                          onChange={e =>
                            handleUpdateElement(index, {
                              ...element,
                              excludeIfWorked: e.target.checked,
                            })
                          }
                          data-test={`input-formula-eventpath-exclude-${index}`}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Exclure si temps travaillé sur la journée
                      </label>
                    </Form.Field>
                  </Box>
                )}

                {element.type === 'operator' && (
                  <Form.Select
                    value={element.op}
                    onChange={e =>
                      handleUpdateElement(index, {
                        type: 'operator',
                        op: e.target.value as 'add' | 'sub',
                      })
                    }
                    disabled={disabled}
                    data-test={`input-formula-operator-${index}`}
                  >
                    <option value="add">+ Addition</option>
                    <option value="sub">- Soustraction</option>
                  </Form.Select>
                )}
              </Columns.Column>

              <Columns.Column size={1}>
                <Button
                  color="danger"
                  size="small"
                  onClick={() => handleRemoveElement(index)}
                  disabled={disabled}
                  data-test={`input-formula-remove-${index}`}
                >
                  ✕
                </Button>
              </Columns.Column>
            </Columns>
          ))}
        </Box>
      )}

      {/* Add element buttons */}
      <Columns>
        <Columns.Column>
          <Button
            size="small"
            onClick={() => handleAddElement('path')}
            disabled={disabled || elementsValidation.expectedNext !== 'operand'}
            fullwidth
            data-test="input-formula-add-path"
          >
            📍 Ajouter une valeur fixe
          </Button>
        </Columns.Column>
        <Columns.Column>
          <Button
            size="small"
            onClick={() => handleAddElement('rule')}
            disabled={
              disabled ||
              availableRules.length === 0 ||
              elementsValidation.expectedNext !== 'operand'
            }
            fullwidth
            title={
              availableRules.length === 0
                ? "Aucune règle disponible (créez d'abord d'autres règles)"
                : 'Ajouter une référence à une autre règle'
            }
            data-test="input-formula-add-rule"
          >
            📋 Ajouter une Règle Employeur
          </Button>
        </Columns.Column>
        <Columns.Column>
          <Button
            size="small"
            onClick={() => handleAddElement('eventPath')}
            disabled={
              disabled ||
              availableEvents.length === 0 ||
              elementsValidation.expectedNext !== 'operand'
            }
            fullwidth
            title={
              availableEvents.length === 0
                ? "Aucun événement prestation disponible (créez d'abord des événements de type prestation)"
                : 'Ajouter une valeur filtrée par événement prestation'
            }
            data-test="input-formula-add-event-path"
          >
            🏷️ Ajouter un Événement
          </Button>
        </Columns.Column>
        <Columns.Column>
          <Button
            size="small"
            onClick={() => handleAddElement('operator')}
            disabled={disabled || elementsValidation.expectedNext !== 'operator'}
            fullwidth
            title={
              elementsValidation.expectedNext !== 'operator'
                ? "Ajoutez d'abord un élément (path ou règle) puis ajoutez un opérateur."
                : 'Ajouter un opérateur entre deux éléments'
            }
            data-test="input-formula-add-operator"
          >
            ➕ Ajouter un Opérateur
          </Button>
        </Columns.Column>
      </Columns>
    </Box>
  )
}

export default FormulaBuilder
